/**
 * Worlds Gallery
 *
 * A gallery of "worlds" — each derived from a Machina playset. Players browse,
 * search, filter, and sort, then jump straight into campaign creation prefilled
 * with the chosen world's playset.
 *
 * Visual language matches CampaignLobby's CampaignCard: deterministic placeholder
 * cover (no external fetch), gold corner ornaments, hover scale/glow, gold CTA.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  PLAYSETS,
  Playset,
  PlaysetId,
  MagicLevel,
  TechLevel,
  FunType,
  FUN_TYPE_LABELS,
  STRUCTURE_TYPE_LABELS,
} from '@machina/core';
import { GouldCorner } from '../Ornaments';
import { Icon } from '../Icon';

const GOLD = '#d4af37';
const GOLD_DIM = 'rgba(212, 175, 55, 0.3)';
const SILVER = '#c0c0c0';
const SILVER_DIM = 'rgba(192, 192, 192, 0.35)';

/**
 * Machina's flagship setting. In the default (unfiltered) view it gets the
 * featured treatment: a larger card, gold accent, and the only gold CTA among
 * the worlds. Gold is reserved for "act here" — see the gold-economy notes below.
 */
const FEATURED_ID: PlaysetId = 'tethered_city';

interface WorldsGalleryProps {
  onStartWorld: (playsetId: PlaysetId) => void;
  /**
   * Pre-login front door: relabel the world CTAs as a signup invite ("Sign up to
   * play this world") since acting hands off to signup rather than creating now.
   * Still a single gold CTA on the featured card — preserves the gold economy.
   */
  loggedOut?: boolean;
}

// =============================================================================
// DERIVED METADATA
// =============================================================================

interface GenreTag {
  label: string;
  color: string;
  bg: string;
}

/** Shared, uniform silver styling for every genre/tone tag (no per-genre hue). */
const SILVER_TAG: Pick<GenreTag, 'color' | 'bg'> = {
  color: SILVER,
  bg: 'transparent',
};

const MAGIC_LEVEL_SHORT: Record<MagicLevel, string> = {
  low: 'Low Magic',
  medium: 'Medium Magic',
  wild: 'Wild Magic',
};

const TECH_LEVEL_SHORT: Record<TechLevel, string> = {
  bronze: 'Bronze Age',
  medieval: 'Medieval',
  renaissance: 'Renaissance',
  industrial: 'Industrial',
  steampunk: 'Steampunk',
};

/**
 * Derive a single headline genre/tone tag for a world from its profile.
 * Priority: distinctive tech (steampunk/bronze) → grimdark/heroic tone →
 * wild magic → fallback "Fantasy".
 *
 * Color economy: ALL genre/tone tags render uniform silver/achromatic (silver
 * text + subtle silver-dim border, transparent fill). No lavender, no gold on
 * any tag — gold is reserved for "act here" and the featured card's badge/edge.
 * Only the label varies; styling is shared via the SILVER_TAG constant.
 */
function deriveGenreTag(playset: Playset): GenreTag {
  const tech = playset.profile.setting?.tech_level;
  const magic = playset.profile.setting?.magic_level;
  const grimdark = playset.profile.tone?.noblebright_grimdark ?? 50;
  const lethal = playset.profile.tone?.heroic_lethal ?? 50;

  let label = 'Fantasy';
  if (tech === 'steampunk') label = 'Steampunk';
  else if (tech === 'bronze') label = 'Mythic';
  else if (grimdark >= 70) label = 'Grimdark';
  else if (lethal >= 60) label = 'Lethal';
  else if (grimdark <= 35) label = 'Heroic';
  else if (magic === 'wild') label = 'Wild Magic';

  return { label, ...SILVER_TAG };
}

/** Small metadata chips for the card footer. */
function deriveChips(playset: Playset): string[] {
  const chips: string[] = [];
  const magic = playset.profile.setting?.magic_level;
  const tech = playset.profile.setting?.tech_level;
  const structure = playset.profile.structure;
  const primaryFun = playset.profile.funTypes?.[0] as FunType | undefined;

  if (magic) chips.push(MAGIC_LEVEL_SHORT[magic]);
  if (tech) chips.push(TECH_LEVEL_SHORT[tech]);
  if (primaryFun) chips.push(FUN_TYPE_LABELS[primaryFun].label);
  if (structure) chips.push(STRUCTURE_TYPE_LABELS[structure].label);

  return chips;
}

/**
 * Deterministic cover gradient + pattern derived from the playset id.
 * No external image fetch — purely CSS, so it always renders instantly.
 */
function getCoverStyle(playset: Playset): React.CSSProperties {
  if (playset.coverImage) {
    return {
      backgroundImage: `linear-gradient(135deg, rgba(10,8,12,0.2), rgba(10,8,12,0.7)), url(${playset.coverImage})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    };
  }

  const hash = playset.id.split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  const hue = hash % 360;
  const hue2 = (hue + 40) % 360;

  return {
    backgroundImage: [
      `radial-gradient(circle at 30% 25%, hsla(${hue}, 45%, 32%, 0.55), transparent 55%)`,
      `radial-gradient(circle at 75% 70%, hsla(${hue2}, 50%, 28%, 0.5), transparent 50%)`,
      `linear-gradient(135deg, #14100c 0%, #1f1812 50%, #14100c 100%)`,
      // Subtle gold knotwork lattice
      `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 20h40M20 0v40' stroke='%23d4af37' stroke-opacity='0.06' stroke-width='1'/%3E%3Ccircle cx='20' cy='20' r='6' fill='none' stroke='%23d4af37' stroke-opacity='0.08'/%3E%3C/svg%3E")`,
    ].join(', '),
    backgroundSize: 'cover, cover, cover, 40px 40px',
  };
}

// =============================================================================
// FILTER / SORT
// =============================================================================

type SortKey = 'name' | 'tone';

// Tech-level filter chips (a small, sensible set). Only worth the chrome once the
// gallery grows past this many worlds; below it the search + sort are enough.
const TECH_FILTERS: { id: TechLevel; label: string }[] = [
  { id: 'bronze', label: 'Bronze Age' },
  { id: 'medieval', label: 'Medieval' },
  { id: 'renaissance', label: 'Renaissance' },
  { id: 'steampunk', label: 'Steampunk' },
];

/** Show the genre/tech filter chips only when the gallery is large enough. */
const FILTER_CHIP_THRESHOLD = 8;

// =============================================================================
// RESPONSIVE
// =============================================================================

/**
 * True on narrow viewports. Inline styles can't carry media queries, so the
 * featured/2x2 layout reads this to collapse to a single column on mobile.
 * Breakpoint chosen so a ~390px phone collapses, while tablets keep 2 columns.
 */
function useIsNarrow(maxWidthPx = 720): boolean {
  const query = `(max-width: ${maxWidthPx}px)`;
  const [narrow, setNarrow] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false
  );

  useEffect(() => {
    const mq = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setNarrow(e.matches);
    setNarrow(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [query]);

  return narrow;
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function WorldsGallery({ onStartWorld, loggedOut = false }: WorldsGalleryProps) {
  const isNarrow = useIsNarrow();
  const [search, setSearch] = useState('');
  const [activeTech, setActiveTech] = useState<Set<TechLevel>>(new Set());
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [hovered, setHovered] = useState<PlaysetId | null>(null);

  const allPlaysets = useMemo(() => Object.values(PLAYSETS), []);

  // For a small gallery the filter chips are more chrome than help — only show
  // them once there are enough worlds to warrant filtering. When hidden, the
  // active-tech filter is effectively inert (it's never toggled on).
  const showChips = allPlaysets.length > FILTER_CHIP_THRESHOLD;

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();

    let list = allPlaysets.filter((p) => {
      // Search by name / tagline / description
      if (q) {
        const haystack = `${p.name} ${p.tagline} ${p.description}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      // Tech-level filter (multi-select, OR semantics)
      if (activeTech.size > 0) {
        const tech = p.profile.setting?.tech_level;
        if (!tech || !activeTech.has(tech)) return false;
      }
      return true;
    });

    list = [...list].sort((a, b) => {
      if (sortKey === 'name') return a.name.localeCompare(b.name);
      // Sort by tone: noblebright (low) → grimdark (high)
      const ta = a.profile.tone?.noblebright_grimdark ?? 50;
      const tb = b.profile.tone?.noblebright_grimdark ?? 50;
      return ta - tb;
    });

    return list;
  }, [allPlaysets, search, activeTech, sortKey]);

  const toggleTech = (tech: TechLevel) => {
    setActiveTech((prev) => {
      const next = new Set(prev);
      if (next.has(tech)) next.delete(tech);
      else next.add(tech);
      return next;
    });
  };

  // Default view = no search text AND no active filter chip. Only then do we use
  // the "featured + 2x2" editorial layout. Searching/filtering falls back to the
  // uniform responsive grid so results stay predictable.
  const isDefaultView = search.trim() === '' && activeTech.size === 0;
  const featured =
    isDefaultView ? visible.find((p) => p.id === FEATURED_ID) ?? null : null;
  const rest = featured ? visible.filter((p) => p.id !== featured.id) : visible;

  return (
    <section style={styles.wrap}>
      <div style={styles.headingRow}>
        <h2 style={styles.heading}>
          <Icon name="compass" size={22} color={GOLD} />
          Explore Worlds
        </h2>
        <span style={styles.count}>
          {visible.length} {visible.length === 1 ? 'world' : 'worlds'}
        </span>
      </div>

      {/* Controls bar */}
      <div style={styles.controls}>
        {/* Search */}
        <div style={styles.searchWrap}>
          <Icon name="eye" size={16} color="var(--color-text-muted)" style={{ flexShrink: 0 }} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search worlds..."
            style={styles.searchInput}
          />
          {search && (
            <button onClick={() => setSearch('')} style={styles.clearBtn} title="Clear search">
              <Icon name="close" size={14} />
            </button>
          )}
        </div>

        {/* Tech filter chips — only for larger galleries (see FILTER_CHIP_THRESHOLD) */}
        {showChips && (
          <div style={styles.filterChips}>
            {TECH_FILTERS.map((f) => {
              const on = activeTech.has(f.id);
              return (
                <button
                  key={f.id}
                  onClick={() => toggleTech(f.id)}
                  style={{
                    ...styles.filterChip,
                    borderColor: on ? GOLD : 'var(--color-border)',
                    backgroundColor: on ? 'rgba(212, 175, 55, 0.15)' : 'transparent',
                    color: on ? GOLD : 'var(--color-text-secondary)',
                  }}
                >
                  {f.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Sort dropdown */}
        <div style={styles.sortWrap}>
          <label style={styles.sortLabel}>Sort</label>
          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value as SortKey)}
            style={styles.sortSelect}
          >
            <option value="name">Name A–Z</option>
            <option value="tone">By Tone (Hopeful → Grim)</option>
          </select>
        </div>
      </div>

      {/* Grid */}
      {visible.length > 0 ? (
        featured ? (
          // Default editorial layout: one large featured world on top, the
          // remaining four in a balanced 2x2 grid (single column on mobile).
          <div style={styles.featuredLayout}>
            <WorldCard
              key={featured.id}
              playset={featured}
              featured
              loggedOut={loggedOut}
              isHovered={hovered === featured.id}
              onHover={() => setHovered(featured.id)}
              onLeave={() => setHovered(null)}
              onStart={() => onStartWorld(featured.id)}
            />
            <div
              style={{
                ...styles.restGrid,
                gridTemplateColumns: isNarrow ? '1fr' : 'repeat(2, minmax(0, 1fr))',
              }}
            >
              {rest.map((playset) => (
                <WorldCard
                  key={playset.id}
                  playset={playset}
                  loggedOut={loggedOut}
                  isHovered={hovered === playset.id}
                  onHover={() => setHovered(playset.id)}
                  onLeave={() => setHovered(null)}
                  onStart={() => onStartWorld(playset.id)}
                />
              ))}
            </div>
          </div>
        ) : (
          // Searching / filtering: uniform responsive grid, no featured treatment.
          <div style={styles.grid}>
            {visible.map((playset) => (
              <WorldCard
                key={playset.id}
                playset={playset}
                loggedOut={loggedOut}
                isHovered={hovered === playset.id}
                onHover={() => setHovered(playset.id)}
                onLeave={() => setHovered(null)}
                onStart={() => onStartWorld(playset.id)}
              />
            ))}
          </div>
        )
      ) : (
        <div style={styles.emptyState}>
          <Icon name="map" size={40} color={GOLD_DIM} />
          <p style={styles.emptyText}>No worlds match your search.</p>
          <button
            onClick={() => {
              setSearch('');
              setActiveTech(new Set());
            }}
            style={styles.resetBtn}
          >
            Clear filters
          </button>
        </div>
      )}
    </section>
  );
}

// =============================================================================
// WORLD CARD
// =============================================================================

interface WorldCardProps {
  playset: Playset;
  isHovered: boolean;
  onHover: () => void;
  onLeave: () => void;
  onStart: () => void;
  /** The flagship world. Gets the gold accent + gold CTA; all others stay silver. */
  featured?: boolean;
  /** Pre-login: relabel the CTA as a signup invite (still one gold CTA on featured). */
  loggedOut?: boolean;
}

function WorldCard({ playset, isHovered, onHover, onLeave, onStart, featured = false, loggedOut = false }: WorldCardProps) {
  const genre = useMemo(() => deriveGenreTag(playset), [playset]);
  const chips = useMemo(() => deriveChips(playset), [playset]);
  const cover = useMemo(() => getCoverStyle(playset), [playset]);

  // Gold accent only on the featured card; every other card uses a silver edge so
  // gold reads as "act here" rather than ambient decoration.
  const accent = featured ? GOLD : GOLD_DIM;
  const cornerColor = featured ? GOLD : GOLD_DIM;

  // Shorten the CTA on mobile so it stays one line ("…in this World" wraps at 390px).
  // Logged-out visitors see a signup invite — acting hands off to signup.
  const isNarrow = useIsNarrow();
  const ctaLabel = loggedOut
    ? isNarrow
      ? 'Sign up to play'
      : 'Sign up to play this world'
    : isNarrow
      ? 'Start Campaign'
      : 'Start Campaign in this World';

  return (
    <div
      style={{
        ...styles.card,
        ...(featured ? styles.cardFeatured : null),
        transform: isHovered ? 'translateY(-4px) scale(1.02)' : 'translateY(0) scale(1)',
        boxShadow: isHovered
          ? featured
            ? `0 14px 48px rgba(0, 0, 0, 0.55), 0 0 28px ${GOLD_DIM}`
            : `0 12px 40px rgba(0, 0, 0, 0.5), 0 0 20px ${SILVER_DIM}`
          : featured
            ? `0 6px 28px rgba(0, 0, 0, 0.45), 0 0 16px ${GOLD_DIM}`
            : '0 4px 20px rgba(0, 0, 0, 0.4)',
        borderColor: isHovered ? accent : featured ? GOLD : GOLD_DIM,
      }}
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
    >
      {/* Cover */}
      <div style={{ ...styles.cover, ...(featured ? styles.coverFeatured : null), ...cover }}>
        <div style={styles.coverOverlay} />
        {/* Featured badge — the only gold dot/label in the gallery */}
        {featured && (
          <div style={styles.featuredBadge}>
            <span style={styles.featuredDot} />
            Featured
          </div>
        )}
        {/* Genre/tone tag — uniform silver, subtle silver-dim border, no fill. */}
        <div style={{ ...styles.genreTag, color: genre.color, backgroundColor: genre.bg, borderColor: SILVER_DIM }}>
          {genre.label}
        </div>
      </div>

      {/* Content */}
      <div style={styles.content}>
        <h4 style={{ ...styles.title, ...(featured ? styles.titleFeatured : null) }}>{playset.name}</h4>
        <div style={styles.tagline}>{playset.tagline}</div>
        <p style={{ ...styles.description, ...(featured ? styles.descriptionFeatured : null) }}>
          {playset.description}
        </p>

        {/* Metadata chips */}
        <div style={styles.metaChips}>
          {chips.map((chip) => (
            <span key={chip} style={styles.metaChip}>
              {chip}
            </span>
          ))}
        </div>

        {/* CTA — gold only on the featured card; silver ghost button elsewhere */}
        {featured ? (
          <button style={{ ...styles.cta, opacity: isHovered ? 1 : 0.9 }} onClick={onStart}>
            <Icon name="play" size={14} />
            {ctaLabel}
          </button>
        ) : (
          <button
            style={{
              ...styles.ctaGhost,
              borderColor: isHovered ? SILVER : SILVER_DIM,
              color: isHovered ? '#e8e8e8' : SILVER,
              backgroundColor: isHovered ? 'rgba(192, 192, 192, 0.08)' : 'transparent',
            }}
            onClick={onStart}
          >
            <Icon name="play" size={14} />
            {ctaLabel}
          </button>
        )}
      </div>

      {/* Corner ornaments */}
      <GouldCorner size={20} color={cornerColor} style={{ position: 'absolute', top: 4, left: 4, opacity: 0.6 }} />
      <GouldCorner size={20} color={cornerColor} style={{ position: 'absolute', top: 4, right: 4, transform: 'scaleX(-1)', opacity: 0.6 }} />
    </div>
  );
}

// =============================================================================
// STYLES
// =============================================================================

const styles: Record<string, React.CSSProperties> = {
  wrap: {
    width: '100%',
    maxWidth: 1200,
    margin: '0 auto',
  },

  headingRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 'var(--spacing-lg)',
  },

  heading: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-sm)',
    margin: 0,
    fontSize: 'var(--font-size-lg)',
    fontFamily: 'var(--font-display)',
    fontWeight: 400,
    color: GOLD,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
  },

  count: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-muted)',
    letterSpacing: '0.05em',
  },

  controls: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 'var(--spacing-md)',
    marginBottom: 'var(--spacing-xl)',
    padding: 'var(--spacing-md)',
    backgroundColor: 'var(--color-surface)',
    border: `1px solid ${GOLD_DIM}`,
    borderRadius: 'var(--radius-md)',
  },

  searchWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-sm)',
    flex: '1 1 220px',
    minWidth: 180,
    padding: 'var(--spacing-xs) var(--spacing-sm)',
    backgroundColor: 'var(--color-background)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-sm)',
  },

  searchInput: {
    flex: 1,
    minWidth: 0,
    background: 'transparent',
    border: 'none',
    outline: 'none',
    color: 'var(--color-text)',
    fontSize: 'var(--font-size-sm)',
    fontFamily: 'var(--font-body)',
  },

  clearBtn: {
    background: 'transparent',
    border: 'none',
    color: 'var(--color-text-muted)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    padding: 0,
  },

  filterChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 'var(--spacing-xs)',
  },

  filterChip: {
    padding: '4px 12px',
    borderRadius: '999px',
    border: '1px solid var(--color-border)',
    fontSize: 'var(--font-size-xs)',
    fontFamily: 'var(--font-body)',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    whiteSpace: 'nowrap',
  },

  sortWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-xs)',
    marginLeft: 'auto',
  },

  sortLabel: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },

  sortSelect: {
    padding: 'var(--spacing-xs) var(--spacing-sm)',
    backgroundColor: 'var(--color-background)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--color-text)',
    fontSize: 'var(--font-size-sm)',
    fontFamily: 'var(--font-body)',
    cursor: 'pointer',
    outline: 'none',
  },

  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: 'var(--spacing-lg)',
    width: '100%',
    boxSizing: 'border-box',
  },

  // Default editorial layout: featured card on top, 2x2 grid below.
  featuredLayout: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-lg)',
    width: '100%',
    boxSizing: 'border-box',
  },

  // The four non-featured worlds: 2x2 on desktop, 1 column on mobile. The actual
  // gridTemplateColumns is set inline from useIsNarrow (inline styles can't hold
  // media queries).
  restGrid: {
    display: 'grid',
    gap: 'var(--spacing-lg)',
    width: '100%',
    boxSizing: 'border-box',
  },

  card: {
    position: 'relative',
    backgroundColor: 'var(--color-surface)',
    borderRadius: 'var(--radius-md)',
    border: `2px solid ${GOLD_DIM}`,
    overflow: 'hidden',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
    display: 'flex',
    flexDirection: 'column',
  },

  // Featured card: gold edge + faint gold inner wash so it reads as the flagship.
  cardFeatured: {
    border: `2px solid ${GOLD}`,
    background:
      'radial-gradient(circle at 50% 0%, rgba(212, 175, 55, 0.07), transparent 55%), var(--color-surface)',
  },

  cover: {
    position: 'relative',
    width: '100%',
    aspectRatio: '16 / 9',
    overflow: 'hidden',
  },

  // Taller, more cinematic cover for the featured world — but capped so the top
  // of the 2x2 grid peeks above the fold (cues "directory, scroll for more"
  // rather than a single hero world filling the viewport).
  coverFeatured: {
    aspectRatio: '21 / 9',
    maxHeight: 400,
  },

  featuredBadge: {
    position: 'absolute',
    top: 12,
    left: 12,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 12px',
    borderRadius: 'var(--radius-sm)',
    border: `1px solid ${GOLD}`,
    backgroundColor: 'rgba(212, 175, 55, 0.15)',
    color: GOLD,
    fontSize: 'var(--font-size-xs)',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    backdropFilter: 'blur(2px)',
  },

  featuredDot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    backgroundColor: GOLD,
    boxShadow: `0 0 8px ${GOLD}`,
  },

  coverOverlay: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(to bottom, transparent 45%, rgba(0, 0, 0, 0.75) 100%)',
  },

  genreTag: {
    position: 'absolute',
    top: 12,
    right: 12,
    padding: '4px 12px',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    backdropFilter: 'blur(2px)',
  },

  content: {
    padding: 'var(--spacing-md)',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },

  title: {
    margin: 0,
    fontSize: 'var(--font-size-lg)',
    fontFamily: 'var(--font-display)',
    fontWeight: 600,
    color: 'var(--color-text)',
  },

  titleFeatured: {
    fontSize: 'var(--font-size-xl)',
  },

  tagline: {
    fontSize: 'var(--font-size-xs)',
    color: GOLD,
    fontStyle: 'italic',
    marginTop: 2,
    marginBottom: 'var(--spacing-sm)',
  },

  description: {
    margin: 0,
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-secondary)',
    lineHeight: 1.5,
    marginBottom: 'var(--spacing-md)',
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },

  // Featured card has more room — show the full pitch.
  descriptionFeatured: {
    WebkitLineClamp: 4,
    maxWidth: 640,
  },

  metaChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 'var(--spacing-md)',
  },

  metaChip: {
    padding: '2px 8px',
    borderRadius: 'var(--radius-sm)',
    backgroundColor: 'rgba(192, 192, 192, 0.08)',
    border: '1px solid var(--color-border)',
    fontSize: '0.7rem',
    color: 'var(--color-text-muted)',
    whiteSpace: 'nowrap',
  },

  // Gold-filled CTA — reserved for the featured world only.
  cta: {
    width: '100%',
    marginTop: 'auto',
    padding: 'var(--spacing-sm) var(--spacing-md)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--spacing-sm)',
    background: `linear-gradient(180deg, ${GOLD} 0%, #a08030 50%, #8b6914 100%)`,
    border: `2px solid ${GOLD}`,
    borderRadius: 'var(--radius-sm)',
    color: '#1a1410',
    fontSize: 'var(--font-size-sm)',
    fontFamily: 'var(--font-display)',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    cursor: 'pointer',
    transition: 'opacity 0.2s ease',
  },

  // Silver ghost CTA — used by every non-featured card so gold stays scarce.
  // Border/text/bg colors are overridden inline on hover.
  ctaGhost: {
    width: '100%',
    marginTop: 'auto',
    padding: 'var(--spacing-sm) var(--spacing-md)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--spacing-sm)',
    background: 'transparent',
    border: `1px solid ${SILVER_DIM}`,
    borderRadius: 'var(--radius-sm)',
    color: SILVER,
    fontSize: 'var(--font-size-sm)',
    fontFamily: 'var(--font-display)',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    cursor: 'pointer',
    transition: 'border-color 0.2s ease, color 0.2s ease, background-color 0.2s ease',
  },

  emptyState: {
    textAlign: 'center',
    padding: 'var(--spacing-xl)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 'var(--spacing-sm)',
  },

  emptyText: {
    margin: 0,
    color: 'var(--color-text-secondary)',
  },

  resetBtn: {
    marginTop: 'var(--spacing-sm)',
    padding: 'var(--spacing-xs) var(--spacing-lg)',
    backgroundColor: 'transparent',
    border: `1px solid ${GOLD_DIM}`,
    borderRadius: 'var(--radius-sm)',
    color: GOLD,
    fontSize: 'var(--font-size-sm)',
    cursor: 'pointer',
  },
};
